/**
 * useResearchAccess.js
 *
 * Liefert die Research-Lizenzen der aktiven World.
 *
 * Die Flags liegen beim Owner der World (Users/{activeWorldId}/…), nicht beim
 * eingeloggten User. Ein Team-Mitarbeiter, der in der World seines Owners
 * arbeitet, bekommt damit exakt dessen Freischaltungen — ohne dass an seinem
 * eigenen Account etwas gesetzt werden muss. Members dürfen Users/{ownerId}
 * lesen (siehe firebase-rules.json).
 *
 * Freigeschaltet wird im Team-Dashboard (PremiumToggle). Features mit
 * `minPlan` sind zusätzlich ab diesem Plan an, solange dort nichts gesetzt ist.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { database } from '../firebaseConfig';
import { useTeam } from '../contexts/TeamContext';
import {
  RESEARCH_FEATURE_KEYS,
  RESEARCH_ALL_LOCKED,
  RESEARCH_PLAN_KEYS,
  resolveResearchFlag,
} from '../config/researchFeatures';
import { resolveMembershipPlanId } from '../config/creditPlans';

export const useResearchAccess = () => {
  const { activeWorldId } = useTeam();

  // null = noch nicht geladen. Ohne diese Unterscheidung würde beim Öffnen
  // eines Research-Dialogs kurz "nicht verfügbar" aufblitzen. flags und planId
  // kommen im selben Commit, damit die Karten nicht erst "Locked" und dann den Plan zeigen.
  const [access, setAccess] = useState(null);

  useEffect(() => {
    if (!activeWorldId) {
      setAccess(null);
      return undefined;
    }

    setAccess(null);
    // Raw flag values (true / false / null). Plan-based features also need the
    // owner's membership plan; the others ignore it.
    const raw = {};
    const plan = RESEARCH_PLAN_KEYS.length ? {} : { creditPlan: null, membershipPlan: null };

    const commit = () => {
      if (RESEARCH_FEATURE_KEYS.some((key) => raw[key] === undefined)) return;
      if (plan.creditPlan === undefined || plan.membershipPlan === undefined) return;
      const planId = resolveMembershipPlanId(plan.creditPlan, { membershipPlan: plan.membershipPlan });
      setAccess({
        flags: RESEARCH_FEATURE_KEYS.reduce(
          (acc, key) => ({ ...acc, [key]: resolveResearchFlag(key, raw[key], planId) }),
          {}
        ),
        planId,
      });
    };

    const listen = (path, onData) => onValue(
      ref(database, path),
      (snapshot) => { onData(snapshot.val()); commit(); },
      () => { onData(null); commit(); }
    );

    const unsubscribers = RESEARCH_FEATURE_KEYS.map((key) =>
      listen(`Users/${activeWorldId}/${key}`, (val) => { raw[key] = typeof val === 'boolean' ? val : null; })
    );
    if (RESEARCH_PLAN_KEYS.length) {
      unsubscribers.push(
        listen(`World/${activeWorldId}/creditPlan`, (val) => { plan.creditPlan = val; }),
        listen(`Users/${activeWorldId}/membershipPlan`, (val) => { plan.membershipPlan = val; })
      );
    }

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [activeWorldId]);

  const accountEmail = getAuth().currentUser?.email || null;

  return useMemo(
    () => ({
      research: access?.flags || RESEARCH_ALL_LOCKED,
      researchLoading: !!activeWorldId && access === null,
      planId: access?.planId || null,
      researchContext: { accountEmail, worldId: activeWorldId },
    }),
    [access, activeWorldId, accountEmail]
  );
};

/**
 * Lizenz-Check als Ref — für Research-Handler, die in useCallback stecken.
 *
 * Der Wert landet dadurch nicht in den Dependency-Arrays, die Handler bleiben
 * stabil und lesen trotzdem immer den aktuellen Stand. Gedacht als letzte
 * Sicherung direkt an der Ausführung: Die Dialoge sperren gesperrte Tools
 * bereits, aber ein künftiger Button soll die Sperre nicht versehentlich
 * umgehen können.
 */
export const useResearchLicenseRef = (featureKey) => {
  const { research, researchLoading } = useResearchAccess();
  // Startwert true: Solange die Lizenzen laden, wird nicht blockiert.
  const licensedRef = useRef(true);

  useEffect(() => {
    licensedRef.current = researchLoading || research[featureKey] === true;
  }, [research, researchLoading, featureKey]);

  return licensedRef;
};

export default useResearchAccess;
